/* Whether this is the installed iPhone or iPad app (Home Screen web app), as opposed to Android, a browser tab or
   a desktop window. Only there does the app arrange its history and its drawer swipe around iOS's own edge-swipe
   "back" (DIRECTION: phone gestures); everywhere else nothing changes. */

export type StandaloneProbe = {
  /** Safari's own flag: true in a Home Screen app. */
  standalone?: boolean | undefined;
  /** `matchMedia("(display-mode: standalone)").matches` */
  displayStandalone?: boolean | undefined;
  userAgent?: string | undefined;
  platform?: string | undefined;
  maxTouchPoints?: number | undefined;
};

/** An iPhone, an iPod or an iPad, including an iPad (or a touch Mac) that says it is a Mac. */
export function isIosDevice(p: StandaloneProbe): boolean {
  if (/iPhone|iPad|iPod/.test(p.userAgent ?? "")) return true;
  return p.platform === "MacIntel" && (p.maxTouchPoints ?? 0) > 1;
}

/** `navigator.standalone === true`, or the standalone display mode on an iOS device. */
export function isInstalledIos(p: StandaloneProbe): boolean {
  if (p.standalone === true) return true;
  return p.displayStandalone === true && isIosDevice(p);
}

let cached: boolean | undefined;

/** The probe on this device, read once. */
export function installedIos(): boolean {
  if (cached !== undefined) return cached;
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  cached = isInstalledIos({
    standalone: (navigator as Navigator & { standalone?: boolean }).standalone,
    displayStandalone: window.matchMedia?.("(display-mode: standalone)").matches,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
  });
  return cached;
}
